import ky, { HTTPError } from "ky";
import { isObject } from "radash";

import type { Metadata } from "@solvari/argus";
import type { LocaleMorpheus } from "@solvari/translations";

import { ziggyUnsafe as ziggy } from "@solvari/common-fe/integrations";
import {
  isError,
  networkErrorToSentry,
  returnNetworkError,
  ValidationError,
} from "@solvari/utils";

type SubmitData = Metadata & SubmitApplicationData & SubmitLeadData;

type SubmitApplicationData = {
  affiliateId: number | null;
  campaignId: number | null;
  iframeId: number | null;
};

type SubmitLeadData = {
  city: string | null;
  company: string;
  creditDeviceId: number | null;
  email: string;
  firstName: string;

  houseNumber: string | null;
  lastName: string;

  localeCode: LocaleMorpheus;
  phone: string;
  products: number[];
  registrationNumber: string | null;
  street: string | null;

  termsAndConditions: boolean;
  zipcode: string | null;
};

class UserAlreadyAssignedToCustomerException extends HTTPError {
  constructor(cause: HTTPError) {
    super(cause.response, cause.request, cause.options);
    this.name = "UserAlreadyAssignedToCustomerException";
    this.cause = cause;
  }
}

const submit = {
  mutation: async (
    submitData: App.Http.Requests.Api.V4.Pro.RegistrationFormSubmissionRequest,
  ) => {
    const url = ziggy("api.v4.pro.registration.store");
    const result = await returnNetworkError(() =>
      ky
        .post(url, {
          json: submitData,
          timeout: 20000,
        })
        .json<{
          customerId: number;
          redirectUrl?: string;
          shouldRedirectWindow?: boolean;
          type: "affiliate" | "lead";
        }>(),
    )();

    if (result instanceof ValidationError) {
      const body: unknown = await result.response.json();
      if (
        isObject(body) &&
        "message" in body &&
        body.message === "User already assigned to customer"
      ) {
        return new UserAlreadyAssignedToCustomerException(result);
      }
    }

    if (isError(result)) {
      networkErrorToSentry(result);
    }

    return result;
  },
};

export type { SubmitApplicationData, SubmitData, SubmitLeadData };
export { submit, UserAlreadyAssignedToCustomerException };
